import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  Alert,
  Badge,
  Table,
  ButtonGroup,
  Tabs,
  Tab,
  ProgressBar,
  Spinner
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import QuestionBankBrowser from './QuestionBankBrowser';
import QuestionBankService from '../services/questionBankService';

const QuizBuilder = () => {
  const { currentUser, userProfile } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [activeTab, setActiveTab] = useState('list');
  const [showQuestionBank, setShowQuestionBank] = useState(false);

  // Non-responsive learner tracking
  const [nonResponsiveSettings, setNonResponsiveSettings] = useState({
    trackingEnabled: true,
    inactivityThreshold: 7, // days
    autoNotifications: true,
    escalationEnabled: true,
    escalationThreshold: 14, // days
    reminderFrequency: 3 // days
  });

  const [nonResponsiveLearners, setNonResponsiveLearners] = useState([]);

  // Quiz form state
  const [quizForm, setQuizForm] = useState({
    title: '',
    description: '',
    moduleId: '',
    timeLimit: 30,
    passingScore: 70,
    maxAttempts: 3,
    isActive: true,
    instructions: '',
    showResultsImmediately: true,
    shuffleQuestions: false,
    shuffleOptions: false,
    autoGrading: true,
    dueDate: '',
    dueTime: '23:59',
    googleFormsIntegration: false,
    googleFormsUrl: '',
    notificationSettings: {
      dueDateReminder: true,
      autoSubmissionNotification: true,
      gradeReleaseNotification: true
    }
  });

  // Question form state
  const [questionForm, setQuestionForm] = useState({
    type: 'multiple-choice',
    question: '',
    options: ['', '', '', ''],
    correctAnswers: [],
    points: 1,
    explanation: '',
    difficulty: 'medium',
    tags: []
  });

  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (currentUser && (userProfile?.role === 'facilitator' || userProfile?.role === 'admin')) {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load quizzes created by this facilitator
      const quizzesQuery = query(
        collection(db, 'quizzes'),
        where('createdBy', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const quizzesSnapshot = await getDocs(quizzesQuery);
      const quizzesData = quizzesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));

      // Load modules for dropdown
      const modulesQuery = query(
        collection(db, 'modules'),
        where('createdBy', '==', currentUser.uid)
      );
      const modulesSnapshot = await getDocs(modulesQuery);
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setQuizzes(quizzesData);
      setModules(modulesData);

    } catch (error) {
      devError('Error loading data:', error);
      showAlert('Error loading data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const resetQuizForm = () => {
    setQuizForm({
      title: '',
      description: '',
      moduleId: '',
      timeLimit: 30,
      passingScore: 70,
      maxAttempts: 3,
      isActive: true,
      instructions: '',
      showResultsImmediately: true,
      shuffleQuestions: false,
      shuffleOptions: false,
      autoGrading: true,
      dueDate: '',
      dueTime: '23:59',
      googleFormsIntegration: false,
      googleFormsUrl: '',
      notificationSettings: {
        dueDateReminder: true,
        autoSubmissionNotification: true,
        gradeReleaseNotification: true
      }
    });
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      type: 'multiple-choice',
      question: '',
      options: ['', '', '', ''],
      correctAnswers: [],
      points: 1,
      explanation: '',
      difficulty: 'medium',
      tags: []
    });
  };

  const handleCreateQuiz = () => {
    resetQuizForm();
    setEditingQuiz(null);
    setShowQuizModal(true);
  };

  const handleEditQuiz = (quiz) => {
    setQuizForm({ ...quiz });
    setEditingQuiz(quiz);
    setShowQuizModal(true);
  };

  const handleSaveQuiz = async () => {
    if (!quizForm.title.trim() || !quizForm.description.trim()) {
      showAlert('Please fill in title and description', 'warning');
      return;
    }

    try {
      const quizData = {
        ...quizForm,
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        dueDate: quizForm.dueDate ? new Date(`${quizForm.dueDate}T${quizForm.dueTime}`) : null
      };

      if (editingQuiz) {
        // Update existing quiz
        const quizRef = doc(db, 'quizzes', editingQuiz.id);
        await updateDoc(quizRef, quizData);
        showAlert('Quiz updated successfully', 'success');
      } else {
        // Create new quiz
        quizData.createdAt = serverTimestamp();
        quizData.questions = [];
        quizData.totalPoints = 0;
        quizData.questionCount = 0;

        await addDoc(collection(db, 'quizzes'), quizData);
        showAlert('Quiz created successfully', 'success');

        // Schedule due date notifications if enabled
        if (quizData.dueDate && quizData.notificationSettings.dueDateReminder) {
          await scheduleDueDateNotifications(quizData);
        }
      }

      setShowQuizModal(false);
      loadData();
    } catch (error) {
      devError('Error saving quiz:', error);
      showAlert('Error saving quiz', 'danger');
    }
  };

  const scheduleDueDateNotifications = async (quizData) => {
    try {
      // Schedule notifications for 24 hours and 1 hour before due date
      const notificationTimes = [
        new Date(quizData.dueDate.getTime() - 24 * 60 * 60 * 1000), // 24 hours before
        new Date(quizData.dueDate.getTime() - 60 * 60 * 1000) // 1 hour before
      ];

      for (const notificationTime of notificationTimes) {
        await addDoc(collection(db, 'scheduledNotifications'), {
          type: 'quiz_due_reminder',
          quizId: quizData.id || 'new',
          quizTitle: quizData.title,
          scheduledFor: notificationTime,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid
        });
      }
    } catch (error) {
      devError('Error scheduling notifications:', error);
    }
  };

  const autoGradeQuiz = (quizData, studentAnswers) => {
    let totalScore = 0;
    let maxScore = 0;
    const results = [];

    quizData.questions.forEach((question, index) => {
      maxScore += question.points || 1;
      const studentAnswer = studentAnswers[index];
      let isCorrect = false;
      let pointsEarned = 0;

      switch (question.type) {
        case 'multiple-choice':
          isCorrect = studentAnswer === question.correctAnswers[0];
          pointsEarned = isCorrect ? (question.points || 1) : 0;
          break;
        case 'checkbox':
          const correctSet = new Set(question.correctAnswers);
          const studentSet = new Set(studentAnswer || []);
          isCorrect = correctSet.size === studentSet.size &&
                     [...correctSet].every(answer => studentSet.has(answer));
          pointsEarned = isCorrect ? (question.points || 1) : 0;
          break;
        case 'true-false':
          isCorrect = studentAnswer === question.correctAnswers[0];
          pointsEarned = isCorrect ? (question.points || 1) : 0;
          break;
        default:
          // For open-ended questions, require manual grading
          pointsEarned = 0;
          isCorrect = null;
      }

      totalScore += pointsEarned;
      results.push({
        questionIndex: index,
        isCorrect,
        pointsEarned,
        studentAnswer,
        explanation: question.explanation || ''
      });
    });

    const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
    const passed = percentage >= (quizData.passingScore || 70);

    return {
      totalScore,
      maxScore,
      percentage,
      passed,
      results,
      gradedAt: new Date()
    };
  };

  const handleDeleteQuiz = async (quizId) => {
    if (window.confirm('Are you sure you want to delete this quiz? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'quizzes', quizId));
        showAlert('Quiz deleted successfully', 'warning');
        loadData();
      } catch (error) {
        devError('Error deleting quiz:', error);
        showAlert('Error deleting quiz', 'danger');
      }
    }
  };

  const handleManageQuestions = (quiz) => {
    setCurrentQuiz(quiz);
    setActiveTab('questions');
  };

  const handleAddQuestion = () => {
    resetQuestionForm();
    setEditingQuestion(null);
    setShowQuestionModal(true);
  };

  const handleEditQuestion = (question, index) => {
    setQuestionForm({ ...question });
    setEditingQuestion({ ...question, index });
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.question.trim()) {
      showAlert('Please enter a question', 'warning');
      return;
    }

    if (questionForm.type === 'multiple-choice' && questionForm.correctAnswers.length === 0) {
      showAlert('Please select at least one correct answer', 'warning');
      return;
    }

    try {
      const quizRef = doc(db, 'quizzes', currentQuiz.id);
      let updatedQuestions = [...(currentQuiz.questions || [])];
      
      if (editingQuestion && editingQuestion.index !== undefined) {
        // Update existing question
        updatedQuestions[editingQuestion.index] = {
          ...questionForm,
          id: editingQuestion.id || Date.now().toString()
        };
      } else {
        // Add new question
        updatedQuestions.push({
          ...questionForm,
          id: Date.now().toString()
        });
      }

      const totalPoints = updatedQuestions.reduce((sum, q) => sum + (q.points || 1), 0);

      await updateDoc(quizRef, {
        questions: updatedQuestions,
        questionCount: updatedQuestions.length,
        totalPoints: totalPoints,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setCurrentQuiz({
        ...currentQuiz,
        questions: updatedQuestions,
        questionCount: updatedQuestions.length,
        totalPoints: totalPoints
      });

      setShowQuestionModal(false);
      showAlert('Question saved successfully', 'success');
      loadData();
    } catch (error) {
      devError('Error saving question:', error);
      showAlert('Error saving question', 'danger');
    }
  };

  const handleDeleteQuestion = async (questionIndex) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        const quizRef = doc(db, 'quizzes', currentQuiz.id);
        const updatedQuestions = currentQuiz.questions.filter((_, index) => index !== questionIndex);
        const totalPoints = updatedQuestions.reduce((sum, q) => sum + (q.points || 1), 0);

        await updateDoc(quizRef, {
          questions: updatedQuestions,
          questionCount: updatedQuestions.length,
          totalPoints: totalPoints,
          updatedAt: serverTimestamp()
        });

        setCurrentQuiz({
          ...currentQuiz,
          questions: updatedQuestions,
          questionCount: updatedQuestions.length,
          totalPoints: totalPoints
        });

        showAlert('Question deleted successfully', 'warning');
        loadData();
      } catch (error) {
        devError('Error deleting question:', error);
        showAlert('Error deleting question', 'danger');
      }
    }
  };

  // Question Bank Functions
  const handleImportFromBank = (selectedQuestions) => {
    if (!currentQuiz) {
      showAlert('Please select or create a quiz first', 'warning');
      return;
    }

    try {
      const quizRef = doc(db, 'quizzes', currentQuiz.id);
      let updatedQuestions = [...(currentQuiz.questions || [])];

      // Import selected questions
      selectedQuestions.forEach(bankQuestion => {
        updatedQuestions.push({
          type: bankQuestion.type || bankQuestion.questionType || 'multiple-choice',
          question: bankQuestion.question,
          options: bankQuestion.options || [],
          correctAnswers: bankQuestion.correctAnswers || [],
          points: bankQuestion.points || 1,
          explanation: bankQuestion.explanation || '',
          difficulty: bankQuestion.difficulty || 'medium',
          tags: bankQuestion.tags || [],
          id: Date.now().toString() + Math.random().toString(36).substring(7),
          sourceQuestionBankId: bankQuestion.id // Track origin
        });
      });

      const totalPoints = updatedQuestions.reduce((sum, q) => sum + (q.points || 1), 0);

      updateDoc(quizRef, {
        questions: updatedQuestions,
        questionCount: updatedQuestions.length,
        totalPoints: totalPoints,
        updatedAt: serverTimestamp()
      });

      setCurrentQuiz({
        ...currentQuiz,
        questions: updatedQuestions,
        questionCount: updatedQuestions.length,
        totalPoints: totalPoints
      });

      showAlert(`Imported ${selectedQuestions.length} question(s) successfully`, 'success');
      loadData();
    } catch (error) {
      devError('Error importing questions:', error);
      showAlert('Error importing questions', 'danger');
    }
  };

  const handleSaveToBank = async (question) => {
    try {
      const questionBankService = new QuestionBankService();

      // Get category from current quiz or module
      const category = currentQuiz?.title || '';

      const result = await questionBankService.addQuestion({
        question: question.question,
        type: question.type,
        questionType: question.type,
        options: question.options || [],
        correctAnswers: question.correctAnswers || [],
        explanation: question.explanation || '',
        category: category,
        tags: question.tags || [],
        difficulty: question.difficulty || 'medium',
        points: question.points || 1,
        isPublic: true
      }, currentUser.uid, currentUser.displayName || currentUser.email);

      if (result.success) {
        showAlert('Question saved to bank successfully', 'success');
      } else {
        showAlert('Error saving to bank: ' + result.error, 'danger');
      }
    } catch (error) {
      devError('Error saving to bank:', error);
      showAlert('Error saving to bank', 'danger');
    }
  };

  const handleOptionChange = (optionIndex, value) => {
    const newOptions = [...questionForm.options];
    newOptions[optionIndex] = value;
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  const handleCorrectAnswerToggle = (optionIndex) => {
    let newCorrectAnswers = [...questionForm.correctAnswers];
    
    if (questionForm.type === 'multiple-choice') {
      // Single correct answer for multiple choice
      newCorrectAnswers = [optionIndex];
    } else {
      // Multiple correct answers for checkbox
      const index = newCorrectAnswers.indexOf(optionIndex);
      if (index > -1) {
        newCorrectAnswers.splice(index, 1);
      } else {
        newCorrectAnswers.push(optionIndex);
      }
    }
    
    setQuestionForm({ ...questionForm, correctAnswers: newCorrectAnswers });
  };

  const addOption = () => {
    if (questionForm.options.length < 6) {
      setQuestionForm({
        ...questionForm,
        options: [...questionForm.options, '']
      });
    }
  };

  const removeOption = (index) => {
    if (questionForm.options.length > 2) {
      const newOptions = questionForm.options.filter((_, i) => i !== index);
      const newCorrectAnswers = questionForm.correctAnswers
        .map(ans => ans > index ? ans - 1 : ans)
        .filter(ans => ans < newOptions.length);
      
      setQuestionForm({
        ...questionForm,
        options: newOptions,
        correctAnswers: newCorrectAnswers
      });
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !questionForm.tags.includes(newTag.trim())) {
      setQuestionForm({
        ...questionForm,
        tags: [...questionForm.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setQuestionForm({
      ...questionForm,
      tags: questionForm.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const getModuleName = (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    return module ? module.title : 'No Module';
  };

  const getDifficultyBadgeVariant = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'danger';
      default: return 'secondary';
    }
  };

  if (userProfile?.role !== 'facilitator' && userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access the quiz builder.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading quiz data...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {alert.show && (
        <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false, message: '', type: '' })}>
          {alert.message}
        </Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="list" title="Quiz List">
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2>Quiz Builder</h2>
                  <p className="text-muted">Create and manage quizzes for your modules</p>
                </div>
                <Button variant="primary" onClick={handleCreateQuiz}>
                  <i className="fas fa-plus me-2"></i>Create New Quiz
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            {quizzes.length === 0 ? (
              <Col>
                <Card className="text-center py-5">
                  <Card.Body>
                    <h4 className="text-muted">No Quizzes Created Yet</h4>
                    <p className="text-muted">Create your first quiz to get started with assessments</p>
                    <Button variant="primary" onClick={handleCreateQuiz}>
                      Create Your First Quiz
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ) : (
              quizzes.map(quiz => (
                <Col md={6} lg={4} key={quiz.id} className="mb-4">
                  <Card className="h-100">
                    <Card.Header className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">{quiz.title}</h6>
                        <small className="text-muted">{getModuleName(quiz.moduleId)}</small>
                      </div>
                      <Badge bg={quiz.isActive ? 'success' : 'secondary'}>
                        {quiz.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column">
                      <p className="text-muted small flex-grow-1">{quiz.description}</p>
                      
                      <div className="mb-3">
                        <small className="text-muted">
                          <strong>{quiz.questionCount || 0}</strong> questions • 
                          <strong> {quiz.totalPoints || 0}</strong> points • 
                          <strong> {quiz.timeLimit}</strong> minutes
                        </small>
                        <br />
                        <small className="text-muted">
                          Passing Score: <strong>{quiz.passingScore}%</strong> • 
                          Max Attempts: <strong>{quiz.maxAttempts}</strong>
                        </small>
                      </div>

                      <ButtonGroup className="w-100">
                        <Button variant="outline-primary" size="sm" onClick={() => handleManageQuestions(quiz)}>
                          Questions
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => handleEditQuiz(quiz)}>
                          Edit
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteQuiz(quiz.id)}>
                          Delete
                        </Button>
                      </ButtonGroup>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Tab>

        <Tab eventKey="questions" title="Question Management" disabled={!currentQuiz}>
          {currentQuiz && (
            <>
              <Row className="mb-4">
                <Col>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h3>{currentQuiz.title}</h3>
                      <p className="text-muted">{currentQuiz.description}</p>
                      <div>
                        <Badge bg="info" className="me-2">{currentQuiz.questionCount || 0} Questions</Badge>
                        <Badge bg="warning" className="me-2">{currentQuiz.totalPoints || 0} Total Points</Badge>
                        <Badge bg="success">{currentQuiz.timeLimit} Minutes</Badge>
                      </div>
                    </div>
                    <ButtonGroup>
                      <Button variant="primary" onClick={handleAddQuestion}>
                        <i className="fas fa-plus me-2"></i>Add Question
                      </Button>
                      <Button variant="outline-primary" onClick={() => setShowQuestionBank(true)}>
                        <i className="fas fa-bank me-2"></i>Import from Bank
                      </Button>
                    </ButtonGroup>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Card>
                    <Card.Header>
                      <h5>Questions ({currentQuiz.questions?.length || 0})</h5>
                    </Card.Header>
                    <Card.Body>
                      {currentQuiz.questions && currentQuiz.questions.length > 0 ? (
                        <div className="space-y-3">
                          {currentQuiz.questions.map((question, index) => (
                            <Card key={index} className="mb-3">
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div className="flex-grow-1">
                                    <h6>Question {index + 1}</h6>
                                    <p className="mb-2">{question.question}</p>
                                    <div className="mb-2">
                                      <Badge bg="secondary" className="me-2">{question.type}</Badge>
                                      <Badge bg={getDifficultyBadgeVariant(question.difficulty)} className="me-2">
                                        {question.difficulty}
                                      </Badge>
                                      <Badge bg="info" className="me-2">{question.points} points</Badge>
                                    </div>

                                    {question.type === 'multiple-choice' && (
                                      <div className="mt-2">
                                        {question.options?.map((option, optIndex) => (
                                          <div key={optIndex} className={`p-2 mb-1 rounded ${question.correctAnswers?.includes(optIndex) ? 'bg-success bg-opacity-10 border border-success' : 'bg-light'}`}>
                                            <small>
                                              {String.fromCharCode(65 + optIndex)}. {option}
                                              {question.correctAnswers?.includes(optIndex) && (
                                                <i className="fas fa-check text-success ms-2"></i>
                                              )}
                                            </small>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {question.tags && question.tags.length > 0 && (
                                      <div className="mt-2">
                                        {question.tags.map((tag, tagIndex) => (
                                          <Badge key={tagIndex} bg="outline-secondary" className="me-1">
                                            {tag}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  <ButtonGroup>
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() => handleSaveToBank(question)}
                                      title="Save to Question Bank"
                                    >
                                      <i className="fas fa-save me-1"></i>Save to Bank
                                    </Button>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleEditQuestion(question, index)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDeleteQuestion(index)}
                                    >
                                      Delete
                                    </Button>
                                  </ButtonGroup>
                                </div>

                                {question.explanation && (
                                  <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
                                    <small>
                                      <strong>Explanation:</strong> {question.explanation}
                                    </small>
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <h5 className="text-muted">No Questions Added Yet</h5>
                          <p className="text-muted">Add your first question to get started</p>
                          <Button variant="primary" onClick={handleAddQuestion}>
                            Add First Question
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Tab>

        <Tab eventKey="tracking" title="Learner Tracking">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Non-Responsive Learner Tracking</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Enable automatic tracking of non-responsive learners"
                        checked={nonResponsiveSettings.trackingEnabled}
                        onChange={(e) => setNonResponsiveSettings(prev => ({
                          ...prev,
                          trackingEnabled: e.target.checked
                        }))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Inactivity Threshold (days)</Form.Label>
                      <Form.Control
                        type="number"
                        value={nonResponsiveSettings.inactivityThreshold}
                        onChange={(e) => setNonResponsiveSettings(prev => ({
                          ...prev,
                          inactivityThreshold: parseInt(e.target.value)
                        }))}
                        min="1"
                        max="30"
                      />
                      <Form.Text className="text-muted">
                        Flag learners who haven't logged in or attempted quizzes
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Auto-send reminder emails"
                        checked={nonResponsiveSettings.autoNotifications}
                        onChange={(e) => setNonResponsiveSettings(prev => ({
                          ...prev,
                          autoNotifications: e.target.checked
                        }))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Reminder Frequency (days)</Form.Label>
                      <Form.Control
                        type="number"
                        value={nonResponsiveSettings.reminderFrequency}
                        onChange={(e) => setNonResponsiveSettings(prev => ({
                          ...prev,
                          reminderFrequency: parseInt(e.target.value)
                        }))}
                        min="1"
                        max="14"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Escalate to facilitators after extended inactivity"
                        checked={nonResponsiveSettings.escalationEnabled}
                        onChange={(e) => setNonResponsiveSettings(prev => ({
                          ...prev,
                          escalationEnabled: e.target.checked
                        }))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Escalation Threshold (days)</Form.Label>
                      <Form.Control
                        type="number"
                        value={nonResponsiveSettings.escalationThreshold}
                        onChange={(e) => setNonResponsiveSettings(prev => ({
                          ...prev,
                          escalationThreshold: parseInt(e.target.value)
                        }))}
                        min="7"
                        max="60"
                      />
                    </Form.Group>

                    <Button variant="success">Save Tracking Settings</Button>
                  </Form>
                </Col>

                <Col md={6}>
                  <Alert variant="warning">
                    <h6>Current Non-Responsive Learners</h6>
                    <p>Criteria: No login/session attendance >1 week</p>

                    <div className="tracked-learners">
                      <div className="d-flex justify-content-between align-items-center p-2 mb-2 bg-light rounded">
                        <div>
                          <strong>Jane Smith</strong><br />
                          <small>Last seen: 8 days ago</small>
                        </div>
                        <Badge bg="warning">Flagged</Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center p-2 mb-2 bg-light rounded">
                        <div>
                          <strong>Mike Johnson</strong><br />
                          <small>Last seen: 12 days ago</small>
                        </div>
                        <Badge bg="danger">Escalated</Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center p-2 mb-2 bg-light rounded">
                        <div>
                          <strong>Sarah Wilson</strong><br />
                          <small>Last seen: 5 days ago</small>
                        </div>
                        <Badge bg="info">At Risk</Badge>
                      </div>
                    </div>
                  </Alert>

                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">📧 Auto-Communication</h6>
                    </Card.Header>
                    <Card.Body>
                      <ul className="small mb-0">
                        <li>Email notifications to flagged learners</li>
                        <li>Facilitator notification for persistent non-response</li>
                        <li>Dashboard highlighting on admin view</li>
                        <li>Weekly summary reports</li>
                        <li>Integration with attendance tracking</li>
                        <li>Quiz attempt monitoring</li>
                        <li>Automated intervention scheduling</li>
                      </ul>
                    </Card.Body>
                  </Card>

                  <Alert variant="info" className="mt-3">
                    <small>
                      <strong>Auto-Grading Features:</strong><br />
                      • Multiple Choice: Immediate scoring<br />
                      • True/False: Instant feedback<br />
                      • Multiple Select: Partial credit available<br />
                      • Open-ended: Manual review workflow<br />
                      • Google Forms: Automatic score import<br />
                      • Due date management with notifications
                    </small>
                  </Alert>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Quiz Modal */}
      <Modal show={showQuizModal} onHide={() => setShowQuizModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Quiz Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={quizForm.title}
                    onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                    placeholder="Enter quiz title"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Module</Form.Label>
                  <Form.Control
                    as="select"
                    value={quizForm.moduleId}
                    onChange={(e) => setQuizForm({ ...quizForm, moduleId: e.target.value })}
                  >
                    <option value="">Select Module (Optional)</option>
                    {modules.map(module => (
                      <option key={module.id} value={module.id}>{module.title}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={quizForm.description}
                onChange={(e) => setQuizForm({ ...quizForm, description: e.target.value })}
                placeholder="Enter quiz description"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={quizForm.instructions}
                onChange={(e) => setQuizForm({ ...quizForm, instructions: e.target.value })}
                placeholder="Enter instructions for students (optional)"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Time Limit (minutes)</Form.Label>
                  <Form.Control
                    type="number"
                    min="5"
                    max="180"
                    value={quizForm.timeLimit}
                    onChange={(e) => setQuizForm({ ...quizForm, timeLimit: parseInt(e.target.value) })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Passing Score (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={quizForm.passingScore}
                    onChange={(e) => setQuizForm({ ...quizForm, passingScore: parseInt(e.target.value) })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Attempts</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="10"
                    value={quizForm.maxAttempts}
                    onChange={(e) => setQuizForm({ ...quizForm, maxAttempts: parseInt(e.target.value) })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="showResultsImmediately"
                  label="Show results immediately after completion"
                  checked={quizForm.showResultsImmediately}
                  onChange={(e) => setQuizForm({ ...quizForm, showResultsImmediately: e.target.checked })}
                  className="mb-3"
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="isActive"
                  label="Quiz is active"
                  checked={quizForm.isActive}
                  onChange={(e) => setQuizForm({ ...quizForm, isActive: e.target.checked })}
                  className="mb-3"
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="shuffleQuestions"
                  label="Shuffle questions"
                  checked={quizForm.shuffleQuestions}
                  onChange={(e) => setQuizForm({ ...quizForm, shuffleQuestions: e.target.checked })}
                  className="mb-3"
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="shuffleOptions"
                  label="Shuffle answer options"
                  checked={quizForm.shuffleOptions}
                  onChange={(e) => setQuizForm({ ...quizForm, shuffleOptions: e.target.checked })}
                  className="mb-3"
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="autoGrading"
                  label="Enable auto-grading (for objective questions)"
                  checked={quizForm.autoGrading}
                  onChange={(e) => setQuizForm({ ...quizForm, autoGrading: e.target.checked })}
                  className="mb-3"
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="googleFormsIntegration"
                  label="Google Forms fallback option"
                  checked={quizForm.googleFormsIntegration}
                  onChange={(e) => setQuizForm({ ...quizForm, googleFormsIntegration: e.target.checked })}
                  className="mb-3"
                />
              </Col>
            </Row>

            {quizForm.googleFormsIntegration && (
              <Form.Group className="mb-3">
                <Form.Label>Google Forms URL (fallback)</Form.Label>
                <Form.Control
                  type="url"
                  value={quizForm.googleFormsUrl}
                  onChange={(e) => setQuizForm({ ...quizForm, googleFormsUrl: e.target.value })}
                  placeholder="https://docs.google.com/forms/d/..."
                />
                <Form.Text className="text-muted">
                  This form will be shown as an alternative if the main quiz system is unavailable
                </Form.Text>
              </Form.Group>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={quizForm.dueDate}
                    onChange={(e) => setQuizForm({ ...quizForm, dueDate: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={quizForm.dueTime}
                    onChange={(e) => setQuizForm({ ...quizForm, dueTime: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Card className="border-info">
              <Card.Header className="bg-info bg-opacity-10">
                <h6 className="mb-0">📢 Notification Settings</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Check
                      type="checkbox"
                      label="Due date reminders"
                      checked={quizForm.notificationSettings.dueDateReminder}
                      onChange={(e) => setQuizForm({
                        ...quizForm,
                        notificationSettings: {
                          ...quizForm.notificationSettings,
                          dueDateReminder: e.target.checked
                        }
                      })}
                      className="mb-2"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Check
                      type="checkbox"
                      label="Auto-submission alerts"
                      checked={quizForm.notificationSettings.autoSubmissionNotification}
                      onChange={(e) => setQuizForm({
                        ...quizForm,
                        notificationSettings: {
                          ...quizForm.notificationSettings,
                          autoSubmissionNotification: e.target.checked
                        }
                      })}
                      className="mb-2"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Check
                      type="checkbox"
                      label="Grade release notifications"
                      checked={quizForm.notificationSettings.gradeReleaseNotification}
                      onChange={(e) => setQuizForm({
                        ...quizForm,
                        notificationSettings: {
                          ...quizForm.notificationSettings,
                          gradeReleaseNotification: e.target.checked
                        }
                      })}
                      className="mb-2"
                    />
                  </Col>
                </Row>
                <small className="text-muted">
                  Students will receive automated notifications for due dates (24h & 1h before),
                  submissions, and when grades are available.
                </small>
              </Card.Body>
            </Card>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuizModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveQuiz}>
            {editingQuiz ? 'Update Quiz' : 'Create Quiz'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Question Modal */}
      <Modal show={showQuestionModal} onHide={() => setShowQuestionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingQuestion ? 'Edit Question' : 'Add New Question'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Question Type</Form.Label>
                  <Form.Control
                    as="select"
                    value={questionForm.type}
                    onChange={(e) => setQuestionForm({ ...questionForm, type: e.target.value })}
                  >
                    <option value="multiple-choice">Multiple Choice</option>
                    <option value="checkbox">Multiple Select</option>
                    <option value="true-false">True/False</option>
                    <option value="short-answer">Short Answer</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Row>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Points</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="10"
                        value={questionForm.points}
                        onChange={(e) => setQuestionForm({ ...questionForm, points: parseInt(e.target.value) })}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Difficulty</Form.Label>
                      <Form.Control
                        as="select"
                        value={questionForm.difficulty}
                        onChange={(e) => setQuestionForm({ ...questionForm, difficulty: e.target.value })}
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Question</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={questionForm.question}
                onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                placeholder="Enter your question here"
              />
            </Form.Group>

            {(questionForm.type === 'multiple-choice' || questionForm.type === 'checkbox') && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label>Answer Options</Form.Label>
                  <Button variant="outline-primary" size="sm" onClick={addOption} disabled={questionForm.options.length >= 6}>
                    Add Option
                  </Button>
                </div>
                
                {questionForm.options.map((option, index) => (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <Form.Check
                      type={questionForm.type === 'multiple-choice' ? 'radio' : 'checkbox'}
                      name="correctAnswer"
                      checked={questionForm.correctAnswers.includes(index)}
                      onChange={() => handleCorrectAnswerToggle(index)}
                      className="me-2"
                    />
                    <Form.Control
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + index)}`}
                      className="me-2"
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeOption(index)}
                      disabled={questionForm.options.length <= 2}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                
                <small className="text-muted">
                  {questionForm.type === 'multiple-choice' 
                    ? 'Select the correct answer' 
                    : 'Select all correct answers'}
                </small>
              </div>
            )}

            {questionForm.type === 'true-false' && (
              <Form.Group className="mb-3">
                <Form.Label>Correct Answer</Form.Label>
                <div>
                  <Form.Check
                    type="radio"
                    name="trueFalse"
                    label="True"
                    checked={questionForm.correctAnswers.includes(0)}
                    onChange={() => setQuestionForm({ ...questionForm, correctAnswers: [0] })}
                    inline
                  />
                  <Form.Check
                    type="radio"
                    name="trueFalse"
                    label="False"
                    checked={questionForm.correctAnswers.includes(1)}
                    onChange={() => setQuestionForm({ ...questionForm, correctAnswers: [1] })}
                    inline
                  />
                </div>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Explanation (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={questionForm.explanation}
                onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                placeholder="Provide an explanation for the correct answer"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <div className="d-flex mb-2">
                <Form.Control
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag"
                  className="me-2"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                />
                <Button variant="outline-secondary" onClick={handleAddTag}>
                  Add
                </Button>
              </div>
              <div>
                {questionForm.tags.map((tag, index) => (
                  <Badge key={index} bg="secondary" className="me-1 mb-1">
                    {tag}
                    <Button
                      variant="link"
                      size="sm"
                      className="text-white p-0 ms-1"
                      onClick={() => handleRemoveTag(tag)}
                      style={{ textDecoration: 'none' }}
                    >
                      ×
                    </Button>
                  </Badge>
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuestionModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveQuestion}>
            {editingQuestion ? 'Update Question' : 'Add Question'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Question Bank Browser Modal */}
      <QuestionBankBrowser
        show={showQuestionBank}
        onHide={() => setShowQuestionBank(false)}
        onSelectQuestions={handleImportFromBank}
        currentUser={currentUser}
      />
    </Container>
  );
};

export default QuizBuilder;